import { CalendarClock } from 'lucide-react';
import { useEffect, useState } from 'react';

import ReportsDialog from '@/components/reports/ReportsDialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { OPEN_REPORTS_EVENT } from '@/components/welcome/api';

import { useFeatures } from '@/hooks/useFeatures';

// Header entry for the scheduled email reports manager. Unlike Dashboard and
// Anomalies (separate pages), this opens an in-place dialog over the chat —
// the user asked for a popup, not a navigation.
/**
 * `hideTrigger`: render only the dialog and its open-event listener. The
 * header no longer has a reports icon — the avatar menu opens the dialog by
 * dispatching OPEN_REPORTS_EVENT, the same event the welcome brief uses.
 */
export default function ReportsNav({ hideTrigger = false }: { hideTrigger?: boolean }) {
  const { features, loading } = useFeatures();
  const [open, setOpen] = useState(false);

  // The welcome brief links "Your report failed to send" straight here.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_REPORTS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_REPORTS_EVENT, onOpen);
  }, []);

  const enabled = !loading && !!features.scheduled_reports;
  if (!enabled) return null;

  if (hideTrigger) return <ReportsDialog open={open} onOpenChange={setOpen} />;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOpen(true)}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Scheduled Reports"
          >
            <CalendarClock className="!size-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Scheduled Reports</TooltipContent>
      </Tooltip>
      <ReportsDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
